import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

/* ============================================================
   THÈMES
   ============================================================ */
const THEMES = {
  dark: {
    page:         { bg: "#0a0a0a",                    color: "#fff" },
    card:         { bg: "rgba(255,255,255,0.03)",      border: "rgba(255,255,255,0.08)" },
    cardHeader:   { bg: "rgba(230,255,62,0.07)",       border: "rgba(230,255,62,0.15)",  color: "#e6ff3e" },
    toolbar:      { bg: "rgba(255,255,255,0.02)",      border: "rgba(255,255,255,0.06)" },
    toolbarLabel: { color: "#64748b" },
    select:       { bg: "rgba(255,255,255,0.05)",      border: "rgba(255,255,255,0.1)",  color: "#fff" },
    input:        { bg: "rgba(255,255,255,0.05)",      border: "rgba(255,255,255,0.1)",  color: "#fff" },
    th:           { bg: "rgba(255,255,255,0.06)",      border: "rgba(255,255,255,0.08)", color: "#94a3b8" },
    td:           { bg: "transparent",                 border: "rgba(255,255,255,0.05)", color: "#e2e8f0" },
    tdAlt:        { bg: "rgba(255,255,255,0.02)" },
    emptyCell:    { color: "#64748b" },
    sectionTitle: { color: "#fff" },
    toggleBtn:    { bg: "rgba(255,255,255,0.06)",      border: "rgba(255,255,255,0.12)", color: "#fff" },
    resetBtn:     { bg: "rgba(255,255,255,0.06)",      border: "rgba(255,255,255,0.1)",  color: "#fff" },
    exportBtn:    { bg: "rgba(96,165,250,0.15)",       border: "rgba(96,165,250,0.4)",   color: "#60a5fa" },
    accent:       "#e6ff3e",
    accentText:   "#000",
    conformite: {
      nonconforme: { color: "#f87171", bg: "rgba(248,113,113,0.1)",  border: "rgba(248,113,113,0.3)" },
      conforme:    { color: "#4ade80", bg: "rgba(74,222,128,0.1)",   border: "rgba(74,222,128,0.3)" },
      default:     { color: "#94a3b8", bg: "transparent",            border: "transparent" },
    },
  },
  light: {
    page:         { bg: "#f1f5f9",    color: "#0f172a" },
    card:         { bg: "#fff",       border: "#e2e8f0" },
    cardHeader:   { bg: "#f8fafc",    border: "#e2e8f0",  color: "#475569" },
    toolbar:      { bg: "#f8fafc",    border: "#e2e8f0" },
    toolbarLabel: { color: "#475569" },
    select:       { bg: "#fff",       border: "#e2e8f0",  color: "#0f172a" },
    input:        { bg: "#fff",       border: "#e2e8f0",  color: "#0f172a" },
    th:           { bg: "#f1f5f9",    border: "#e2e8f0",  color: "#64748b" },
    td:           { bg: "#fff",       border: "#f1f5f9",  color: "#334155" },
    tdAlt:        { bg: "#f8fafc" },
    emptyCell:    { color: "#94a3b8" },
    sectionTitle: { color: "#0f172a" },
    toggleBtn:    { bg: "#fff",       border: "#e2e8f0",  color: "#334155" },
    resetBtn:     { bg: "#fff",       border: "#e2e8f0",  color: "#334155" },
    exportBtn:    { bg: "#dbeafe",    border: "#93c5fd",  color: "#1d4ed8" },
    accent:       "#1d4ed8",
    accentText:   "#fff",
    conformite: {
      nonconforme: { color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
      conforme:    { color: "#166534", bg: "#f0fdf4", border: "#86efac" },
      default:     { color: "#94a3b8", bg: "transparent", border: "transparent" },
    },
  },
};

/* ============================================================
   INJECT CSS DYNAMIQUE
   ============================================================ */
function injectStyles(mode) {
  let el = document.getElementById("cflexion-dynamic-styles");
  if (!el) { el = document.createElement("style"); el.id = "cflexion-dynamic-styles"; document.head.appendChild(el); }

  const lime         = mode === "dark" ? "rgba(230,255,62,0.5)"    : "#1d4ed8";
  const limeShadow   = mode === "dark" ? "rgba(230,255,62,0.1)"    : "rgba(29,78,216,0.1)";
  const inputFocusBg = mode === "dark" ? "rgba(230,255,62,0.04)"   : "rgba(29,78,216,0.04)";
  const btnHover     = mode === "dark" ? "#fff"                     : "#2563eb";
  const btnHoverTx   = mode === "dark" ? "#000"                     : "#fff";
  const btnShadow    = mode === "dark" ? "rgba(230,255,62,0.25)"   : "rgba(29,78,216,0.25)";
  const trHover      = mode === "dark" ? "rgba(255,255,255,0.03)"  : "#f8fafc";

  el.innerHTML = `
    .cflexion-input:focus,
    .cflexion-select:focus {
      border-color: ${lime} !important;
      box-shadow: 0 0 0 3px ${limeShadow} !important;
      background: ${inputFocusBg} !important;
      outline: none;
    }
    .cflexion-btn-accent:hover:not(:disabled) {
      background: ${btnHover} !important;
      color: ${btnHoverTx} !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 6px 20px ${btnShadow} !important;
    }
    .cflexion-btn-accent:active { transform: scale(0.97) !important; }
    .cflexion-btn-reset:hover   { opacity: 0.75; }
    .cflexion-btn-export:hover  { opacity: 0.85; transform: translateY(-1px); }
    .cflexion-toggle-btn:hover  { opacity: 0.8; transform: scale(1.05); }
    .cflexion-tr:hover td       { background: ${trHover} !important; }
    .cflexion-action-btn:hover  {
      background: ${btnHover} !important;
      color: ${btnHoverTx} !important;
      transform: scale(1.03) !important;
    }
    @media (min-width: 640px)  { .cflexion-wrap { padding: 24px 32px 48px !important; } }
    @media (min-width: 900px)  { .cflexion-wrap { padding: 28px 60px 48px !important; } }
    @media (min-width: 1200px) { .cflexion-wrap { padding: 32px 140px 48px !important; } }
  `;
}

/* ============================================================
   HELPERS
   ============================================================ */
function getConformiteStyle(val, T) {
  if (!val) return T.conformite.default;
  const v = String(val).toUpperCase();
  if (v.includes("NON")) return T.conformite.nonconforme;
  if (v.includes("CONFORME")) return T.conformite.conforme;
  return T.conformite.default;
}

function applyFilter(arr, key, value) {
  if (!key || String(value).trim() === "") return arr;
  const needle = String(value).toLowerCase();
  return arr.filter((r) => r[key] != null && String(r[key]).toLowerCase().includes(needle));
}

function asFrNumber(val) {
  if (val == null) return "";
  const n = Number(String(val).replace(",", "."));
  return Number.isFinite(n) ? n.toLocaleString("fr-FR", { useGrouping: false }) : val;
}

function toCsvValue(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function rmAccents(s) {
  return String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ConsultationFlexion() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  /* ── Thème ── */
  const [mode, setMode] = useState(() => localStorage.getItem("qsf-theme") || "dark");
  const T = THEMES[mode];
  useEffect(() => { injectStyles(mode); localStorage.setItem("qsf-theme", mode); }, [mode]);
  const toggleMode = () => setMode((m) => (m === "dark" ? "light" : "dark"));

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");

  const [sortKey,      setSortKey]      = useState("index");
  const [sortDir,      setSortDir]      = useState("asc");
  const [filterKey,    setFilterKey]    = useState("");
  const [filterValue,  setFilterValue]  = useState("");
  const [filterKey2,   setFilterKey2]   = useState("");
  const [filterValue2, setFilterValue2] = useState("");
  const [currentPage,  setCurrentPage]  = useState(1);
  const PAGE_SIZE = 20;

  /* ── Chargement ── */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await apiFetch("/api/qualiteflexion/list");

        if (!r.ok) throw new Error("Erreur serveur");
        const data = await r.json();
        const list = Array.isArray(data) ? data : (data.items || []);
        const mapped = (list || []).map((it, i) => ({
          index:               it.index ?? i + 1,
          Timestamp:                it.Timestamp,
          Operator:            it.Operator,
          ID_Stack:            it.ID_Stack,
          No_test:             it.No_test,
          Typologie:           it.Typologie,
          Observation_avant_essai: it.Observation_avant_essai,
          Observation_apres_essai: it.Observation_apres_essai,
          Bending_Modulus_MPa: it.Bending_Modulus_MPa,
          Stress_Peak_MPa:     it.Stress_Peak_MPa,
          Force_Peak_N:        it.Force_Peak_N,
          Strain_Peak_Perc:    it.Strain_Peak_Perc,
          Conformite16GPa:     it.Conformite16GPa,
          raw: it,
        }));
        if (!cancelled) setRows(mapped);
      } catch (e) { if (!cancelled) setErr(e.message); }
      finally      { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  /* ── Vue filtrée / triée ── */
  const view = useMemo(() => {
    let out = applyFilter([...rows], filterKey, filterValue);
    out     = applyFilter(out, filterKey2, filterValue2);
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => String(a[sortKey] ?? "").localeCompare(String(b[sortKey] ?? ""), undefined, { numeric: true }) * dir);
    return out;
  }, [rows, sortKey, sortDir, filterKey, filterValue, filterKey2, filterValue2]);

  useEffect(() => { setCurrentPage(1); }, [filterKey, filterValue, filterKey2, filterValue2, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(view.length / PAGE_SIZE));
  const pageSafe = Math.min(currentPage, totalPages);
  const paginated = view.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  /* ── Édition ── */
  const handleEdit = (raw) => {
    localStorage.setItem("woodoo_edit_item", JSON.stringify(raw));
    navigate("/qualite/controleStack/flexion", { state: { item: raw, edit: true } });
  };

  /* ── Reset filtres ── */
  const resetFilters = () => {
    setFilterKey(""); setFilterValue("");
    setFilterKey2(""); setFilterValue2("");
    setSortKey("index"); setSortDir("asc");
  };

  /* ── Export CSV ── */
  function handleExportCsv() {
    const headers = [
      "INDEX","Timestamp","OPÉRATEUR","STACK","N° TEST","TYPOLOGIE",
      "OBSERVATION AVANT ESSAI","OBSERVATION APRES ESSAI",
      "MODULE (MPa)","STRESS PEAK (MPa)","FORCE PEAK (N)","STRAIN PEAK (%)",
      "CONFORMITÉ 16GPa",
    ].map(rmAccents);

    const data = view.map((r) => [
      r.index, r.Timestamp, r.Operator, r.ID_Stack, r.No_test, r.Typologie,
      r.Observation_avant_essai, r.Observation_apres_essai,
      asFrNumber(r.Bending_Modulus_MPa), asFrNumber(r.Stress_Peak_MPa),
      asFrNumber(r.Force_Peak_N), asFrNumber(r.Strain_Peak_Perc),
      r.Conformite16GPa,
    ]);

    const csvBody = [headers, ...data]
      .map((row) => row.map(toCsvValue).join(";"))
      .join("\n");

    const blob = new Blob(["\uFEFF", `sep=;\n${csvBody}`], { type: "text/csv;charset=utf-8;" });
    const a    = document.createElement("a");
    const d    = new Date();
    const ts   = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    a.href     = URL.createObjectURL(blob);
    a.download = `qualite_flexion_${ts}.csv`;
    a.click();
  }

  /* ── Styles ── */
  const s = {
    page:         { minHeight: "100vh", background: T.page.bg, color: T.page.color, fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,sans-serif", transition: "background 0.3s,color 0.3s" },
    wrap:         { padding: "20px 16px 48px" },
    topBar:       { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, marginTop: 8 },
    sectionLeft:  { display: "flex", alignItems: "center", gap: 12 },
    sectionBadge: { background: T.accent, color: T.accentText, fontWeight: 900, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 4 },
    sectionTitle: { fontWeight: 900, fontSize: 20, letterSpacing: "0.04em", textTransform: "uppercase", color: T.sectionTitle.color },
    toggleBtn:    { display: "flex", alignItems: "center", gap: 8, background: T.toggleBtn.bg, border: `1px solid ${T.toggleBtn.border}`, color: T.toggleBtn.color, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", transition: "all 0.2s" },

    card:         { background: T.card.bg, border: `1px solid ${T.card.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", transition: "all 0.3s" },
    cardHeader:   { padding: "14px 20px", background: T.cardHeader.bg, borderBottom: `1px solid ${T.cardHeader.border}`, fontWeight: 800, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: T.cardHeader.color },

    toolbar:      { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "14px 16px", borderBottom: `1px solid ${T.toolbar.border}`, background: T.toolbar.bg },
    group:        { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
    toolLabel:    { fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.toolbarLabel.color },
    select:       { height: 34, background: T.select.bg, border: `1px solid ${T.select.border}`, borderRadius: 8, color: T.select.color, padding: "0 10px", fontSize: 12, fontFamily: "inherit", outline: "none", transition: "all 0.2s" },
    input:        { height: 34, background: T.input.bg, border: `1px solid ${T.input.border}`, borderRadius: 8, color: T.input.color, padding: "0 10px", fontSize: 12, fontFamily: "inherit", outline: "none", width: 120, transition: "all 0.2s" },
    btnAccent:    { height: 34, padding: "0 14px", border: "none", background: T.accent, color: T.accentText, fontWeight: 800, fontSize: 12, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },
    btnReset:     { height: 34, padding: "0 14px", background: T.resetBtn.bg, border: `1px solid ${T.resetBtn.border}`, color: T.resetBtn.color, fontWeight: 700, fontSize: 12, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },
    btnExport:    { height: 34, padding: "0 14px", background: T.exportBtn.bg, border: `1px solid ${T.exportBtn.border}`, color: T.exportBtn.color, fontWeight: 700, fontSize: 12, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },

    counter:      { padding: "8px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: T.toolbarLabel.color, borderBottom: `1px solid ${T.toolbar.border}` },
    tableWrap:    { overflowX: "auto" },
    table:        { width: "100%", borderCollapse: "collapse" },
    th:           { background: T.th.bg, color: T.th.color, fontWeight: 700, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${T.th.border}`, padding: "12px 10px", whiteSpace: "nowrap" },
    td:           { borderBottom: `1px solid ${T.td.border}`, padding: "10px 10px", fontSize: 13, color: T.td.color, whiteSpace: "nowrap" },
    emptyCell:    { padding: 32, textAlign: "center", color: T.emptyCell.color, fontSize: 14 },
    actionBtn:    { background: T.accent, color: T.accentText, fontWeight: 800, fontSize: 11, letterSpacing: "0.05em", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },
    error:        { color: "#f87171", padding: "12px 16px", fontWeight: 700, fontSize: 13 },
    pagination:   { display: "flex", justifyContent: "center", alignItems: "center", gap: 6, padding: "16px", flexWrap: "wrap", borderTop: `1px solid ${T.toolbar.border}` },
    pageBtn:      { padding: "7px 13px", borderRadius: 8, border: `1px solid ${T.select.border}`, background: T.select.bg, color: T.select.color, cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: "inherit" },
    pageBtnActive:{ background: T.accent, color: T.accentText, border: `1px solid ${T.accent}` },
    pageInfo:     { fontSize: 12, fontWeight: 700, color: T.toolbarLabel.color, padding: "0 8px" },

    conformeBadge: (val) => {
      const c = getConformiteStyle(val, T);
      return { display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, border: `1px solid ${c.border}`, color: c.color };
    },
  };

  const SORT_OPTIONS = [
    { value: "index",               label: "Index" },
    { value: "Timestamp",                label: "Timestamp" },
    { value: "Operator",            label: "Opérateur" },
    { value: "ID_Stack",            label: "Stack" },
    { value: "No_test",             label: "N° Test" },
    { value: "Typologie",           label: "Typologie" },
    { value: "Bending_Modulus_MPa", label: "Module (MPa)" },
  ];
  const FILTER_OPTIONS = [
    { value: "Timestamp",            label: "Timestamp" },
    { value: "Operator",        label: "Opérateur" },
    { value: "ID_Stack",        label: "Stack" },
    { value: "Typologie",       label: "Typologie" },
    { value: "Conformite16GPa", label: "Conformité 16GPa" },
  ];
  const FILTER2_OPTIONS = [
    { value: "ID_Stack",        label: "Stack" },
    { value: "Typologie",       label: "Typologie" },
    { value: "Conformite16GPa", label: "Conformité 16GPa" },
  ];

  const COLS = [
    "Index","Timestamp","Opérateur","ID Stack","N° Test","Typologie",
    "Obs. avant essai","Obs. après essai",
    "Bending Modulus (MPa)","Stress Peak (MPa)","Force Peak (N)","Strain Peak (%)",
    "Conformité 16GPa","Actions",
  ];

  return (
    <div style={s.page}>
      <MenuBar active="qualite" />

      <div style={s.wrap} className="cflexion-wrap">

        {/* ── Top bar ── */}
        <div style={s.topBar}>
          <div style={s.sectionLeft}>
            <span style={s.sectionBadge}>Qualité</span>
            <span style={s.sectionTitle}>Consultation Flexion</span>
          </div>
          <button style={s.toggleBtn} className="cflexion-toggle-btn" onClick={toggleMode}>
            <span style={{ fontSize: 16 }}>{mode === "dark" ? "☀️" : "🌙"}</span>
            <span>{mode === "dark" ? "Clair" : "Sombre"}</span>
          </button>
        </div>

        {/* ── Card ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>Mesures Flexion 3 Points</div>

          {/* ── Toolbar ── */}
          <div style={s.toolbar}>

            {/* Tri */}
            <div style={s.group}>
              <span style={s.toolLabel}>Trier</span>
              <select style={s.select} className="cflexion-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button style={s.btnAccent} className="cflexion-btn-accent" onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}>
                {sortDir === "asc" ? "↑ Asc" : "↓ Desc"}
              </button>
            </div>

            {/* Filtre 1 */}
            <div style={s.group}>
              <span style={s.toolLabel}>Filtrer</span>
              <select style={s.select} className="cflexion-select" value={filterKey} onChange={(e) => { setFilterKey(e.target.value); setFilterValue(""); }}>
                <option value="">{ui("— colonne —")}</option>
                {FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input style={s.input} className="cflexion-input" value={filterValue} onChange={(e) => setFilterValue(e.target.value)} placeholder="valeur…" />
            </div>

            {/* Filtre 2 */}
            <div style={s.group}>
              <span style={s.toolLabel}>Et</span>
              <select style={s.select} className="cflexion-select" value={filterKey2} onChange={(e) => { setFilterKey2(e.target.value); setFilterValue2(""); }}>
                <option value="">{ui("— colonne —")}</option>
                {FILTER2_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input style={s.input} className="cflexion-input" value={filterValue2} onChange={(e) => setFilterValue2(e.target.value)} placeholder="valeur…" />
            </div>

            <button style={s.btnReset} className="cflexion-btn-reset" onClick={resetFilters}>
              ✕ Réinitialiser
            </button>

            <button style={s.btnExport} className="cflexion-btn-export" onClick={handleExportCsv}>
              ↓ Export CSV
            </button>
          </div>

          {/* ── Compteur ── */}
          <div style={s.counter}>
            {loading
              ? "Chargement…"
              : `${view.length} résultat${view.length > 1 ? "s" : ""} · page ${pageSafe}/${totalPages}`}
          </div>

          {/* ── Table ── */}
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {COLS.map((c) => <th key={c} style={s.th}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={COLS.length} style={s.emptyCell}>{ui("Chargement…")}</td></tr>
                )}

                {!loading && paginated.map((r, i) => (
                  <tr key={`${r.index}-${i}`} className="cflexion-tr" style={{ background: i % 2 === 1 ? T.tdAlt.bg : "transparent" }}>

                    {/* Données brutes */}
                    {[r.index, r.Timestamp, r.Operator, r.ID_Stack, r.No_test, r.Typologie,
                      r.Observation_avant_essai, r.Observation_apres_essai,
                      asFrNumber(r.Bending_Modulus_MPa), asFrNumber(r.Stress_Peak_MPa),
                      asFrNumber(r.Force_Peak_N), asFrNumber(r.Strain_Peak_Perc),
                    ].map((val, ci) => (
                      <td key={ci} style={{ ...s.td, textAlign: "center" }}>{val ?? "—"}</td>
                    ))}

                    {/* Conformité 16GPa */}
                    <td style={{ ...s.td, textAlign: "center" }}>
                      <span style={s.conformeBadge(r.Conformite16GPa)}>
                        {r.Conformite16GPa || "—"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{ ...s.td, textAlign: "center" }}>
                      <button style={s.actionBtn} className="cflexion-action-btn" onClick={() => handleEdit(r.raw)}>
                        {ui("Modifier")}
                      </button>
                    </td>
                  </tr>
                ))}

                {!loading && view.length === 0 && (
                  <tr><td colSpan={COLS.length} style={s.emptyCell}>Aucune donnée</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {!loading && view.length > 0 && (
            <div style={s.pagination}>
              <button
                type="button"
                style={{ ...s.pageBtn, opacity: pageSafe === 1 ? 0.35 : 1 }}
                disabled={pageSafe === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                ← Préc.
              </button>
              <span style={s.pageInfo}>
                {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, view.length)} / {view.length}
              </span>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - pageSafe) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === "…" ? (
                    <span key={`dots-${idx}`} style={s.pageInfo}>…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      style={{ ...s.pageBtn, ...(p === pageSafe ? s.pageBtnActive : {}) }}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                type="button"
                style={{ ...s.pageBtn, opacity: pageSafe === totalPages ? 0.35 : 1 }}
                disabled={pageSafe === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Suiv. →
              </button>
            </div>
          )}

          {err && <div style={s.error}>⚠ {err}</div>}
        </div>
      </div>
    </div>
  );
}
